import { useEffect, useState } from "react";
import { api, userApi } from "../../lib/api";
import { useSession } from "../../lib/session";
import { type CollectionOption } from "./monitorTemplates";

/*
 * The two catalog fetches behind every feed picker — the editor's palette and the
 * screen's region popup mount the same lists from the same hooks, so neither surface
 * can drift into offering a different catalog.
 */

/**
 * Merge Editor's Choice + public + the signed-in user's own collections.
 *
 * Reports `loading` rather than just handing back `[]`, because an empty array is two
 * different situations — still fetching, and fetched with nothing in it — and a palette
 * that cannot tell them apart renders "there are no collections" for the first second of
 * its life. On a slow connection that is what the user sees and believes.
 *
 * `enabled` gates the fetch, not the hook: the wall mounts this the moment its chrome
 * COULD show (hooks cannot be conditional) but a television nobody ever hovers must not
 * fetch a catalog nobody will see, so it hands in false until the first hover. While
 * disabled, `loading` stays true — "not loaded yet" is exactly what that state means.
 */
export function useCollectionOptions(enabled = true): {
  collections: CollectionOption[];
  loading: boolean;
} {
  const user = useSession((s) => s.user);
  const [opts, setOpts] = useState<CollectionOption[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!enabled) return;
    let ok = true;
    setLoading(true);
    Promise.all([
      api
        .listCollections()
        .then((r) => r.collections)
        .catch(() => []),
      user
        ? userApi
            .listPlaylists()
            .then((r) => r.playlists)
            .catch(() => [])
        : Promise.resolve([]),
    ]).then(([cols, mine]) => {
      if (!ok) return;
      const ownIds = new Set(mine.map((p) => p.id));
      setOpts([
        ...cols
          .filter((c) => !ownIds.has(c.id))
          .map((c) => ({
            id: c.id,
            name: c.name,
            clipCount: c.clipCount,
            editorsChoice: c.editorsChoice,
          })),
        ...mine.map((p) => ({
          id: p.id,
          name: p.name,
          clipCount: p.clipCount,
          own: true,
        })),
      ]);
      setLoading(false);
    });
    return () => {
      ok = false;
    };
  }, [user, enabled]);
  return { collections: opts, loading };
}

/**
 * Every approved catalog tag. Ask for the whole vocabulary rather than a top-N: the list
 * is ranked by usage, and a cap silently drops the smallest facet values — tits:huge has
 * 5 clips — so a facet group would render missing a chip with no way to tell.
 *
 * The measured facets (quality, aspect) used to be stripped here, on the grounds that a
 * keyword source built from "1080p" is just "everything". They are kept now because the
 * editor offers every facet group the browse sidebar does, and a group whose values were
 * filtered out of this list does not render at all — an invisible group is a worse answer
 * than a broad one, and the user can simply not pick it.
 */
const TAG_LIMIT = 500;

export function useContentTags(enabled = true): { tags: string[]; loading: boolean } {
  const [tags, setTags] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!enabled) return;
    let ok = true;
    api
      .catalogTags(undefined, TAG_LIMIT)
      .then((r) => {
        if (!ok) return;
        setTags(r.tags.map((t) => t.tag));
      })
      .catch(() => {
        if (ok) setTags([]);
      })
      // `finally`, not a line in each branch: a failed fetch has also stopped loading, and
      // the spinner staying up forever is the worse of the two ways to get this wrong.
      .finally(() => {
        if (ok) setLoading(false);
      });
    return () => {
      ok = false;
    };
  }, [enabled]);
  return { tags, loading };
}
